import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';
import HelpTooltip from '../help/HelpTooltip';
import store from "../store";
import { getColorFromPercentage } from "../utils/colorUtils";

export interface SegmentChartProps extends Omit<SvgIconProps, 'children' | 'viewBox'> {
    percent: number;
    title?: JSX.Element;
    description?: JSX.Element;
    radius?: number;
    strokeWidth?: number;
}

const countToPercent = (
    element: d3.Selection<SVGTextElement, unknown, null, undefined>,
    numberValue: number,
    firstRender: boolean
): void => {
    element
        .transition()
        .delay(firstRender ? 500 : 0)
        .tween("text", function () {
            const interpolator = d3.interpolateNumber(numberValue / 2, numberValue);
            // 'this' is SVGTextElement
            return function (this: SVGTextElement, t: number) {
                d3.select(this)
                    .text(`${numberValue === 100 ? '100' : interpolator(t).toFixed(1)}%`)
                    .style("font-weight", "bold");
            };
        })
        .duration(1000);
};


const SegmentChart: React.FC<SegmentChartProps> = ({
    percent,
    title = 'Title',
    description = 'Description',
    radius = 50,
    strokeWidth = 10,
    ...svgIconProps
}) => {
    const [firstRender,setFirstRender] = React.useState<boolean>(true);

    const computedColor = getColorFromPercentage(percent);
    const ref = useRef<SVGSVGElement | null>(null);
    const innerRadius = radius - strokeWidth / 2;
    const diameter = radius * 2 + strokeWidth;
    const circleStrokeWidth = Math.min(radius / 40, 5);
    const circleRadius = innerRadius + circleStrokeWidth;


    useEffect(() => {
        if (ref.current) {
            const svgElement = d3.select(ref.current);
            svgElement.selectAll("g").remove();
            svgElement
                .attr("viewBox", `0 0 ${diameter} ${diameter}`)
                .append("g")
                .attr("class", "chart")
                .attr(
                    "transform",
                    `translate(${diameter / 2},${diameter / 2})`
                )
                .append("circle")
                .attr("cx", 0)
                .attr("cy", 0)
                .attr("r", circleRadius)
                .attr("stroke", computedColor)
                .style("stroke-width", `${circleStrokeWidth}px`)
                .attr("fill", "transparent");

            const pie = d3.pie<number>().value((d: number) => +d);
            const color = d3.scaleOrdinal<string>().range([computedColor, "transparent"]);
            const data_ready = pie([percent, 100 - percent]);
            const arc = d3
                .arc<d3.PieArcDatum<number>>()
                .innerRadius(radius - strokeWidth - circleStrokeWidth)
                .outerRadius(diameter / 2)
                .startAngle(0);

            svgElement
                .selectAll<SVGGElement, d3.PieArcDatum<number>>(".chart")
                .attr(
                    "transform",
                    `translate(${diameter / 2},${diameter / 2})`
                )
                .selectAll<SVGPathElement, d3.PieArcDatum<number>>("allSlices")
                .data(data_ready)
                .enter()
                .append("path")
                .transition()
                .duration((firstRender ? 0 : 800))
                .delay(firstRender ? 0 : 500)
                .attrTween("d", function (this: SVGPathElement, d: d3.PieArcDatum<number>) {
                    const interpolate = d3.interpolate(0, (percent / 100) * Math.PI * 2);
                    return (t: number) => {
                        d.endAngle = interpolate(t);
                        return arc(d) as string;
                    };
                })
                .attr("fill", (d: d3.PieArcDatum<number>) => color(d.data.toString()));

            countToPercent(svgElement.select(".chart_percent"), percent, firstRender);
        }
        if (firstRender) {
            store.dispatch(()=>setFirstRender(false));
        }
    }, [
        percent,
        radius,
        diameter,
        strokeWidth,
        circleRadius,
        circleStrokeWidth
    ]);

    return (
        <HelpTooltip
            title={
                <span>
                    <strong>{title}</strong>
                    <b />
                    <span>
                        {description}
                    </span>
                </span>
            }
            arrow
        >
            <SvgIcon viewBox={`0 0 ${diameter} ${diameter}`} {...svgIconProps} >
                <g ref={ref as React.Ref<SVGSVGElement>}  >
                    <text className="chart_percent" x="50%" y="50%"
                        dominantBaseline="middle"
                        textAnchor="middle"
                        fill={computedColor}
                        fontWeight="bold"
                    ></text>
                </g>
            </SvgIcon>
        </HelpTooltip>
    );
};

export default SegmentChart;
